// src/pages/Coleta/components/HistoricoColetas.jsx
import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import dayjs from 'dayjs';
import PaginacaoFooter from '../../../components/PaginacaoFooter';
import SerialDispositivo from './SerialDispositivo';
import {
    formatarContagem,
    formatarVariacao,
    rotuloTurno,
    situacaoColeta,
    formatarLog10,
} from '../../../utils/coleta';

/** Contagem da linha, ou o que falta para ela existir. */
function Contagem({ linha, unidade, processando, podeProcessar, onProcessar }) {
    const exp = linha.experimento;
    if (exp?.excluido) {
        return <Typography variant="body2" color="error.main">Ensaio excluído</Typography>;
    }
    if (!exp) {
        return <Typography variant="body2" color="text.secondary">Aguardando tubo</Typography>;
    }
    if (processando) {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Processando…</Typography>
            </Stack>
        );
    }
    if (exp.ausente) {
        return <Chip size="small" color="success" variant="outlined" label="Ausente" />;
    }
    if (Number.isFinite(exp.log10Contagem)) {
        return (
            // Mesmo formato dos cards da comparação: valor e unidade numa linha, log10 embaixo.
            <Box>
                <Stack direction="row" spacing={0.75} alignItems="baseline">
                    <Typography fontWeight={700}>{formatarContagem(exp.log10Contagem)}</Typography>
                    <Typography variant="body2" color="text.secondary">{unidade}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block">
                    {formatarLog10(exp.log10Contagem)}
                </Typography>
            </Box>
        );
    }
    if (String(exp.status).toLowerCase() === 'active') {
        return <Typography variant="body2" fontWeight={600}>Em análise</Typography>;
    }
    return podeProcessar ? (
        <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<Lucide.FlaskConical size={16} />}
            onClick={(e) => {
                e.stopPropagation();
                onProcessar(linha);
            }}
            sx={{ borderRadius: 2 }}
        >
            Processar
        </Button>
    ) : (
        <Typography variant="body2" color="text.secondary">Não processada</Typography>
    );
}

function Tendencia({ variacao }) {
    const theme = useTheme();
    if (!Number.isFinite(variacao)) {
        return <Typography variant="body2" color="text.secondary">Sem comparação anterior</Typography>;
    }
    // Contagem que sobe é piora: cor de atenção; que desce, de melhora.
    const subiu = variacao > 0;
    const cor = subiu ? theme.palette.warning.dark : theme.palette.success.main;
    const Icone = subiu ? Lucide.ArrowUpRight : Lucide.ArrowDownRight;
    return (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: cor }}>
            <Icone size={16} />
            <Typography variant="body2" fontWeight={700} sx={{ color: cor }}>{formatarVariacao(variacao)}</Typography>
        </Stack>
    );
}

function Situacao({ status }) {
    const s = situacaoColeta(status);
    const icone = { aguardando: <Lucide.Hourglass size={14} />, andamento: <Lucide.Activity size={14} />, concluida: <Lucide.CircleCheck size={14} /> }[s.chave];
    return <Chip size="small" color={s.cor} variant="outlined" icon={icone} label={s.rotulo} sx={{ fontWeight: 600 }} />;
}

function Identificacao({ linha, nomePonto, Icone, quebrar = false }) {
    const theme = useTheme();
    return (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main', display: 'flex', flexShrink: 0 }}>
                <Icone size={18} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} noWrap>{linha.identificacao}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap={!quebrar} title={quebrar ? undefined : nomePonto || undefined}>{nomePonto || 'Sem ponto'}</Typography>
                {linha.coleta?.origem && (
                    <Typography variant="caption" color="text.secondary" noWrap={!quebrar} display="block" title={quebrar ? undefined : linha.coleta.origem}>{linha.coleta.origem}</Typography>
                )}
            </Box>
        </Stack>
    );
}

function Coleta({ coleta }) {
    if (!coleta?.dataHora) return '—';
    const quando = dayjs(coleta.dataHora);
    return (
        <Box>
            <Typography fontWeight={600}>{quando.format('DD/MM')}</Typography>
            <Typography variant="caption" color="text.secondary">
                {quando.format('HH:mm')} · {rotuloTurno(coleta.turno)}
            </Typography>
        </Box>
    );
}

/**
 * Histórico das amostras da página. Tabela no desktop, cartões no celular.
 */
export default function HistoricoColetas({
    linhas,
    total,
    page,
    totalPages,
    onPageChange,
    tamanho,
    tamanhos,
    onTamanhoChange,
    carregando,
    unidade,
    Icone,
    nomePonto,
    processando,
    podeAlterar,
    podeProcessar,
    onProcessar,
    onEditar,
    onCancelar,
    onAbrir,
}) {
    const theme = useTheme();
    // Cartoes ate 1200px: com a barra lateral aberta, a tabela (7+ colunas) nao cabe
    // no tablet/notebook pequeno e as colunas da direita ficavam escondidas.
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [menu, setMenu] = useState(null);

    const acoes = (linha) => {
        const lista = [];
        if (podeAlterar) lista.push({ chave: 'editar', rotulo: 'Editar dados da coleta', icone: <Lucide.Pencil size={18} />, fazer: onEditar });
        const exp = linha.experimento;
        if (podeProcessar && exp && !exp.excluido && String(exp.status).toLowerCase() !== 'active' && exp.processado) {
            lista.push({ chave: 'reprocessar', rotulo: 'Reprocessar', icone: <Lucide.RotateCcw size={18} />, fazer: onProcessar });
        }
        if (podeAlterar && linha.status === 'PENDENTE') {
            lista.push({ chave: 'cancelar', rotulo: 'Cancelar registro', icone: <Lucide.Ban size={18} />, fazer: onCancelar, perigo: true });
        }
        return lista;
    };

    const botaoAcoes = (linha) =>
        acoes(linha).length > 0 && (
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    setMenu({ ancora: e.currentTarget, linha });
                }}
                aria-label="Ações"
            >
                <Lucide.MoreVertical size={18} />
            </IconButton>
        );

    const props = (linha) => ({
        linha,
        unidade,
        processando: processando.has(linha.experimento?.id),
        podeProcessar,
        onProcessar,
    });

    return (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 2 }}>
                <Typography fontWeight={700} sx={{ fontSize: '1.1rem' }}>Histórico</Typography>
                <Typography variant="body2" color="text.secondary">
                    {total === 1 ? '1 amostra encontrada' : `${total} amostras encontradas`}
                </Typography>
            </Box>

            {carregando && linhas.length === 0 ? (
                <Stack spacing={1} sx={{ px: 3, pb: 3 }}>
                    {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={64} />)}
                </Stack>
            ) : linhas.length === 0 ? (
                <Box sx={{ px: 3, pb: 4, pt: 1, color: 'text.secondary', textAlign: 'center' }}>
                    <Typography variant="body2">Nenhuma amostra com estes filtros.</Typography>
                </Box>
            ) : isMobile ? (
                <Stack divider={<Divider />}>
                    {linhas.map((linha) => (
                        <Box
                            key={linha.id}
                            onClick={() => onAbrir(linha)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && onAbrir(linha)}
                            sx={{ px: 2, py: 1.75, cursor: 'pointer', '&:active': { bgcolor: 'action.hover' } }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                <Identificacao linha={linha} nomePonto={nomePonto(linha.coleta?.pontoColetaId)} Icone={Icone} quebrar />
                                {botaoAcoes(linha)}
                            </Stack>
                            <Box sx={{ mt: 1 }}>
                                <SerialDispositivo serial={linha.numeroSerie} compacto />
                            </Box>
                            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
                                <Coleta coleta={linha.coleta} />
                                <Contagem {...props(linha)} />
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                                <Tendencia variacao={linha.variacaoPercentual} />
                                <Situacao status={linha.status} />
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 860 }}>
                    <TableHead>
                        <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600, bgcolor: 'action.hover' } }}>
                            <TableCell>Amostra / origem</TableCell>
                            <TableCell>Coleta</TableCell>
                            <TableCell>Dispositivo</TableCell>
                            <TableCell>Resultado</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {linhas.map((linha) => (
                            <TableRow key={linha.id} hover>
                                <TableCell sx={{ maxWidth: 280 }}>
                                    <Identificacao linha={linha} nomePonto={nomePonto(linha.coleta?.pontoColetaId)} Icone={Icone} />
                                </TableCell>
                                <TableCell><Coleta coleta={linha.coleta} /></TableCell>
                                <TableCell><SerialDispositivo serial={linha.numeroSerie} /></TableCell>
                                {/* Tendência sob o valor: a coluna Dispositivo precisava do espaço. */}
                                <TableCell>
                                    <Stack spacing={0.75} alignItems="flex-start">
                                        <Contagem {...props(linha)} />
                                        <Tendencia variacao={linha.variacaoPercentual} />
                                    </Stack>
                                </TableCell>
                                <TableCell><Situacao status={linha.status} /></TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Ver amostra">
                                        <IconButton size="small" onClick={() => onAbrir(linha)} aria-label="Ver amostra">
                                            <Lucide.Eye size={18} />
                                        </IconButton>
                                    </Tooltip>
                                    {botaoAcoes(linha)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                </Box>
            )}

            <Box sx={{ px: { xs: 1, sm: 2 } }}>
                <PaginacaoFooter
                    page={page + 1}
                    totalPages={totalPages}
                    onPageChange={(p) => onPageChange(p - 1)}
                    itemsPerPage={tamanho}
                    onItemsPerPageChange={onTamanhoChange}
                    itemsPerPageOptions={tamanhos}
                    loading={carregando}
                />
            </Box>

            <Menu anchorEl={menu?.ancora} open={Boolean(menu)} onClose={() => setMenu(null)}>
                {menu &&
                    acoes(menu.linha).map((a) => (
                        <MenuItem
                            key={a.chave}
                            dense
                            onClick={() => {
                                setMenu(null);
                                a.fazer(menu.linha);
                            }}
                            sx={a.perigo ? { color: 'error.main' } : undefined}
                        >
                            <ListItemIcon sx={a.perigo ? { color: 'error.main' } : undefined}>{a.icone}</ListItemIcon>
                            {a.rotulo}
                        </MenuItem>
                    ))}
            </Menu>
        </Paper>
    );
}
